import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import { useUserProfile } from '../UserProfileContext';
import { useLiturgical } from '../LiturgicalContext';
import { getLiturgicalColorForDate } from '../utils/liturgical';
import { getItems, createItem, updateItem } from '../fitness/fitnessApi';
import { useRoles } from './useRoles';
import { useCalendar, runwayDaysOf, isRoutine } from './useCalendar';
import { useEventMissions } from './useEventMissions';
import RoleManager from './RoleManager';
import MissionTemplates from './MissionTemplates';
import VaultPicker from './VaultPicker';
import JarvisChat from './JarvisChat';
import { callJarvis, buildSystemPrompt, buildBriefingContext, checkMarketConditions } from './jarvisApi';
import { fetchVaultDocForJarvis } from './vaultDocLoader';
import { searchEmails, getEmail, getEmailAttachments, saveAttachmentToVault, resolveVaultFolderPath, moveEmailToFolder } from './emailApi';
import { useMemory } from './useMemory';
import { useProcedures } from './useProcedures';
import MemoryManager from './MemoryManager';
import '../insurance/Insurance.css';   // the ID card tile
import FIGDashboard from '../components/FIG/FIGDashboard';
import TriageQueue from './TriageQueue';
import RunwayStack, { RunwayBar } from './RunwayStack';
import Intercessions from '../spirit/IntercessionsCard';
import './Centcom.css';

const ADMIN_EMAIL = 'marc@defineholy.com';
const ADMIN_EMAILS = ['marc@defineholy.com', 'angela@defineholy.com'];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Month cell shows this many event pills before folding the rest into one line.
const MONTH_CELL_MAX_EVENTS = 2;

const RUNWAY_OPTIONS = [
  { days: 0, label: 'Off' },
  { days: 3, label: '3 days' },
  { days: 7, label: '1 week' },
  { days: 14, label: '2 weeks' },
];

// Resolve GroceryList internal column names (SharePoint may rename them)
const groceryFieldCache = {};
async function resolveGroceryFields(token) {
  if (groceryFieldCache.resolved) return groceryFieldCache;
  try {
    const GRAPH = 'https://graph.microsoft.com/v1.0';
    const siteRes = await fetch(`${GRAPH}/sites/fragosinsurancegroupllc.sharepoint.com:/sites/DefineHolyFamily:`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const site = await siteRes.json();
    const listsRes = await fetch(
      `${GRAPH}/sites/${site.id}/lists?$filter=displayName eq 'GroceryList'&$select=displayName,id`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const listsData = await listsRes.json();
    const list = (listsData.value || []).find(l => l.displayName.toLowerCase() === 'grocerylist');
    if (!list) return { Store: 'Store', Checked: 'Checked', Priority: 'Priority', resolved: false };
    const colsRes = await fetch(
      `${GRAPH}/sites/${site.id}/lists/${list.id}/columns?$select=displayName,name&$top=100`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const colsData = await colsRes.json();
    const cols = colsData.value || [];
    const find = (display) => {
      const col = cols.find(c => c.displayName.toLowerCase() === display.toLowerCase());
      return col?.name || display;
    };
    groceryFieldCache.Store = find('Store');
    groceryFieldCache.Checked = find('Checked');
    groceryFieldCache.Priority = find('Priority');
    groceryFieldCache.resolved = true;
    console.log('[Kitchen] Grocery field names resolved:', groceryFieldCache);
  } catch {
    groceryFieldCache.Store = 'Store';
    groceryFieldCache.Checked = 'Checked';
    groceryFieldCache.Priority = 'Priority';
  }
  return groceryFieldCache;
}

function formatTime(date) {
  if (!date) return '';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatOffset(minutes) {
  const abs = Math.abs(minutes);
  if (abs >= 1440) return `${Math.round(abs / 1440)}d before`;
  if (abs >= 60) {
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m > 0 ? `${h}h ${m}m before` : `${h}h before`;
  }
  return `${abs}m before`;
}

function formatMonthRange(monday, days) {
  const first = days[0];
  const last = days[6];
  const opts = { month: 'short', day: 'numeric' };
  if (first.getMonth() === last.getMonth()) {
    return `${first.toLocaleDateString('en-US', { month: 'short' })} ${first.getDate()}–${last.getDate()}`;
  }
  return `${first.toLocaleDateString('en-US', opts)} – ${last.toLocaleDateString('en-US', opts)}`;
}

const TIME_OFFSET_OPTIONS = [
  { label: '2h before', value: -120 },
  { label: '1h before', value: -60 },
  { label: '45m before', value: -45 },
  { label: '30m before', value: -30 },
  { label: '15m before', value: -15 },
];

// Prayers — extensible array, add new prayers as one-line additions
const PRAYERS = [
  {
    id: 'ave-maria',
    name: 'Ave Maria',
    text: `Ave María, grátia plena,\nDóminus tecum.\nBenedícta tu in muliéribus,\net benedíctus fructus ventris tui, Iésus.\n\nSancta María, Mater Dei,\nora pro nobis peccatóribus,\nnunc et in hora mortis nostrae.\nAmen.`
  },
  {
    id: 'st-michael',
    name: 'St. Michael',
    text: `Saint Michael the Archangel,\ndefend us in battle.\nBe our defense against the wickedness and snares of the Devil.\nMay God rebuke him, we humbly pray,\nand do thou, O Prince of the heavenly hosts,\nby the power of God, thrust into hell Satan,\nand all the evil spirits,\nwho prowl about the world seeking the ruin of souls.\nAmen.`
  },
];

// Liturgical color from day name — logic now lives in ../utils/liturgical.js
// so the app-wide theme and this card derive from the same rules.

// Local date helpers — avoid UTC timezone mismatch (Central Time)
function localDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function localDateFromISO(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function Centcom({ onOpenInsurance = null } = {}) {
  const { instance, accounts } = useMsal();
  const { nickname, prefs, updatePref } = useUserProfile();
  const { color: liturgicalColor } = useLiturgical();
  const readingsCollapsed = prefs.readingsCollapsed || false;
  const briefingCollapsed = prefs.briefingCollapsed !== undefined ? prefs.briefingCollapsed : true;
  const [weather, setWeather] = useState(null);
  const [missionMode, setMissionMode] = useState(false);
  const [figMode, setFigMode] = useState(false);
  const [triageMode, setTriageMode] = useState(false);
  const [showRoleManager, setShowRoleManager] = useState(false);
  const [openPrayer, setOpenPrayer] = useState(null);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [showRoutine, setShowRoutine] = useState(false); // month view: expand routine recurrences
  const [showTemplates, setShowTemplates] = useState(false);
  const [pickingForEvent, setPickingForEvent] = useState(null);
  const [addingTaskForEvent, setAddingTaskForEvent] = useState(null);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskNotes, setNewTaskNotes] = useState('');
  const [newTaskOffset, setNewTaskOffset] = useState(-30);
  const [newTaskVaultDoc, setNewTaskVaultDoc] = useState('');
  const [newTaskVaultDocId, setNewTaskVaultDocId] = useState('');
  const [showSubtaskVaultPicker, setShowSubtaskVaultPicker] = useState(false);
  const [walkthroughTask, setWalkthroughTask] = useState(null); // task object for Jarvis walkthrough
  const [walkthroughLoading, setWalkthroughLoading] = useState(false);
  const [walkthroughDocContent, setWalkthroughDocContent] = useState(null);
  const [showJarvis, setShowJarvis] = useState(false);
  const [showMemoryManager, setShowMemoryManager] = useState(false);
  const [showSundayReadings, setShowSundayReadings] = useState(false);
  const [linkingPostItForEvent, setLinkingPostItForEvent] = useState(null); // event id
  const [postIts, setPostIts] = useState([]);
  const [groceryItems, setGroceryItems] = useState([]);
  const [kitchenInventory, setKitchenInventory] = useState([]);
  const [todayDinnerPlan, setTodayDinnerPlan] = useState(null);
  const [purchaseAverages, setPurchaseAverages] = useState(null);
  const [briefing, setBriefing] = useState(null); // { text, generatedAt }
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingError, setBriefingError] = useState(null);
  const [readings, setReadings] = useState(null); // { liturgicalDay, verse, reference, url }

  const userEmail = accounts[0]?.username || '';

  // Shared helper: find or create today's cache row, then patch fields
  async function upsertBriefingCache(fields) {
    const token = await getToken();
    const cache = await getItems(token, 'BriefingCache');
    const todayLocal = localDateStr();
    const existing = cache.find(c =>
      (c.UserId || '').toLowerCase() === userEmail.toLowerCase() &&
      localDateFromISO(c.BriefingDate) === todayLocal
    );
    if (existing) {
      await updateItem(token, 'BriefingCache', existing.id, fields);
      return existing.id;
    } else {
      const created = await createItem(token, 'BriefingCache', {
        Title: `Briefing ${new Date().toLocaleDateString()}`,
        BriefingText: '',
        ReadingsText: '',
        BriefingDate: new Date().toISOString(),
        UserId: userEmail,
        ...fields,
      });
      return created.id;
    }
  }
  const isAdmin = userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const {
    roles, allRoles, loading: rolesLoading, error: rolesError,
    toggleFilter, isRoleVisible, addRole, editRole, removeRole,
  } = useRoles(getToken, userEmail);

  const {
    events: allWeekEvents, selectedEvents, eventsForDate, runwayForDate, runwaysForDate, patchEventLocal, selectedDate, setSelectedDate,
    weekDays, weekOffset, setWeekOffset, today,
    loading: calLoading, error: calError, isSameDay, refetch: refetchCalendar, getCalendarPath,
    viewMode, setViewMode, monthOffset, setMonthOffset, monthGridDays, monthYear, monthMonth,
  } = useCalendar(getToken, userEmail);

  // Color for the upcoming Sunday pill/modal — resolved from the date via
  // romcal, same as today's, rather than from a scraped title.
  const sundayColor = useMemo(() => {
    const sun = new Date(today);
    sun.setDate(sun.getDate() + (7 - sun.getDay()) % 7);
    return getLiturgicalColorForDate(sun);
  }, [today]);

  const {
    getMission, getAllTasks, attachTemplate, skipEvent, addSubTask,
    updateTaskStatus, linkPostIt, unlinkPostIt, missions, error: missionError,
    refetch: refetchMissions,
  } = useEventMissions(getToken, userEmail);

  const memory = useMemory(getToken, userEmail);
  const procHook = useProcedures(getToken, userEmail);

  // Fetch Memphis weather from weather.gov (once per session)
  useEffect(() => {
    const CACHE_KEY = 'centcom-weather-memphis';
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try { setWeather(JSON.parse(cached)); return; } catch {}
    }
    (async () => {
      try {
        const pointsRes = await fetch('https://api.weather.gov/points/35.1495,-90.0490');
        if (!pointsRes.ok) return;
        const points = await pointsRes.json();
        const forecastUrl = points?.properties?.forecast;
        if (!forecastUrl) return;
        const fcRes = await fetch(forecastUrl);
        if (!fcRes.ok) return;
        const fc = await fcRes.json();
        const periods = fc?.properties?.periods || [];
        const day = periods.find(p => p.isDaytime);
        const night = periods.find(p => !p.isDaytime);
        if (day && night) {
          const w = { high: day.temperature, low: night.temperature };
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(w));
          setWeather(w);
        }
      } catch (err) {
        console.warn('[Centcom] Weather fetch failed:', err.message);
      }
    })();
  }, []);

  // Run memory cleanup + auto-promotion once on load
  useEffect(() => {
    if (!memory.loading && memory.workingMemories.length > 0) {
      memory.runCleanup();
      memory.autoPromote();
    }
  }, [memory.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load Post-Its and Grocery items for Jarvis context
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        // PostItBoard and GroceryList use their own list resolution,
        // but we can fetch via the generic helper if lists exist
        try {
          const pi = await getItems(token, 'PostItBoard');
          setPostIts(pi.map(it => ({
            id: it.id,
            noteText: it.NoteText || it.Title || '',
            processed: it.Processed || false,
            postedBy: it.Title || '',
            created: it.Created || '',
          })));
        } catch { /* PostItBoard may not exist yet */ }
        try {
          const gl = await getItems(token, 'GroceryList');
          setGroceryItems(gl.map(it => ({
            title: it.Title || '',
            store: it.Store || '',
            checked: it.Checked || false,
          })));
        } catch { /* GroceryList may not exist yet */ }
        try {
          const inv = await getItems(token, 'KitchenInventory');
          // Map old SharePoint ItemType values to current names
          const typeAliases = { 'Whole Loin': 'Ribeye Loin' };
          setKitchenInventory(inv.map(it => {
            let itemType = it.ItemType || it.Title || '';
            if (typeAliases[itemType]) itemType = typeAliases[itemType];
            return { itemType, quantity: it.Quantity || 0, lastUpdated: it.LastUpdated || '' };
          }));
        } catch (err) {
          console.warn('[Centcom] KitchenInventory fetch failed:', err.message);
        }
        try {
          const dinners = await getItems(token, 'DinnerOrders');
          const todayISO = new Date().toISOString().split('T')[0];
          const todayPlan = dinners.find(d => d.OrderDate === todayISO);
          if (todayPlan) {
            setTodayDinnerPlan({
              headcount: todayPlan.Headcount || 5,
              protein: todayPlan.Protein || 'Steak',
              dinnerTime: todayPlan.DinnerTime || '5:00 PM',
              guestNames: todayPlan.GuestNames || '',
            });
          }
        } catch (err) {
          console.warn('[Centcom] DinnerOrders fetch failed:', err.message);
        }
        try {
          const purchases = await getItems(token, 'PurchaseLog');
          const byType = {};
          purchases.forEach(p => {
            const type = p.ItemType || p.Title || '';
            const qty = p.Quantity || 0;
            const cost = p.Cost || 0;
            if (!type || qty <= 0) return;
            if (!byType[type]) byType[type] = { totalCost: 0, totalQty: 0 };
            byType[type].totalCost += cost;
            byType[type].totalQty += qty;
          });
          const avgs = {};
          Object.entries(byType).forEach(([type, data]) => {
            avgs[type] = {
              avgCostPerUnit: data.totalQty > 0 ? data.totalCost / data.totalQty : 0,
              totalSpent: data.totalCost,
              totalPurchased: data.totalQty,
            };
          });
          setPurchaseAverages(avgs);
        } catch (err) {
          console.warn('[Centcom] PurchaseLog fetch failed:', err.message);
        }
        // Load cached briefing + readings for today
        try {
          const cache = await getItems(token, 'BriefingCache');
          const todayLocal = localDateStr();
          const myCache = cache.find(c =>
            (c.UserId || '').toLowerCase() === userEmail.toLowerCase() &&
            localDateFromISO(c.BriefingDate) === todayLocal
          );
          if (myCache && myCache.BriefingText) {
            setBriefing({ text: myCache.BriefingText, generatedAt: new Date(myCache.BriefingDate) });
          }
        } catch { /* BriefingCache may not exist yet */ }
      } catch { /* token error — not critical */ }
    })();
  }, [getToken, userEmail]);

  // Daily readings — TEMPORARILY bypassing cache, always fetching fresh from USCCB
  useEffect(() => {
    if (!getToken || !userEmail) return;
    (async () => {
      console.log('[Readings] Effect started — cache disabled, always fetching fresh');
      try {
        setReadings(null);
        const readingsData = await fetchReadings();
        if (readingsData) {
          setReadings(readingsData);
          console.log('[Readings] Fresh fetch complete');
        }
      } catch (err) {
        console.warn('[Readings] Fetch failed:', err.message);
      }
    })();
  }, [getToken, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const jarvisContext = {
    nickname,
    today,
    events: eventsForDate(today),
    weekEvents: allWeekEvents,
    missions,
    roles,
    postIts,
    groceryItems,
    kitchenInventory,
    todayDinnerPlan,
    purchaseAverages,
    memoryContext: memory.buildMemoryContext(),
  };

  async function handlePickTemplate(tmpl, event) {
    await attachTemplate(event, tmpl);
    setPickingForEvent(null);
  }

  async function handleSkipMission(ev) {
    await skipEvent(ev);
  }

  // Replace the event's single Runway* category (or drop it for days=0),
  // leaving every other category alone. Optimistic, then reconcile.
  async function handleSetRunway(ev, days) {
    const calPath = getCalendarPath();
    if (!calPath) return;
    const kept = (ev.categories || []).filter(c => !runwayDaysOf({ categories: [c] }));
    const categories = days ? [...kept, `Runway${days}`] : kept;
    patchEventLocal(ev.id, { categories });
    try {
      const token = await getToken();
      const res = await fetch(
        `https://graph.microsoft.com/v1.0${calPath.base}/calendars/${calPath.id}/events/${ev.id}`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ categories }),
        }
      );
      if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => '')}`);
    } catch (err) {
      console.warn('[Runway] Category update failed:', err.message);
    }
    refetchCalendar();
  }

  // Band click behaves like tapping the event: jump to its day and expand it.
  function openRunwayEvent(rw) {
    setSelectedDate(rw.event.start);
    setExpandedEventId(rw.event.id);
  }

  async function handleAddSubTask(eventId) {
    if (!newTaskName.trim()) return;
    await addSubTask(eventId, {
      name: newTaskName.trim(),
      notes: newTaskNotes.trim(),
      timeOffset: newTaskOffset,
      vaultDocPath: newTaskVaultDoc,
      vaultDocId: newTaskVaultDocId,
    });
    setNewTaskName('');
    setNewTaskNotes('');
    setNewTaskOffset(-30);
    setNewTaskVaultDoc('');
    setNewTaskVaultDocId('');
    setAddingTaskForEvent(null);
  }

  async function fetchReadings() {
    const dateStr = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const yy = String(today.getFullYear()).slice(-2);
    const usccbUrl = `https://bible.usccb.org/bible/readings/${mm}${dd}${yy}.cfm`;
    const fallback = {
      liturgicalDay: `Daily Readings — ${dateStr}`,
      readings: [{ name: 'View today\'s readings on USCCB', url: usccbUrl }],
      url: usccbUrl,
    };

    // Step 1: Fetch USCCB page via Netlify proxy for the authoritative liturgical day
    let todayData = null;
    try {
      const res = await fetch(`/api/usccb-proxy?date=${mm}${dd}${yy}`);
      if (res.ok) todayData = await res.json();
    } catch (err) {
      console.warn('[Readings] USCCB proxy failed:', err.message);
    }

    if (!todayData?.title) {
      console.warn('[Readings] No USCCB title, using fallback');
      return fallback;
    }

    console.log('[Readings] USCCB title:', todayData.title);

    // Fetch Sunday USCCB page if today is not Sunday
    const isSunday = today.getDay() === 0;
    let sundayTitle = '';
    let sundayUrl = '';
    let sundayDate = '';
    let sundayBody = '';
    if (!isSunday) {
      try {
        const sun = new Date(today);
        sun.setDate(sun.getDate() + (7 - sun.getDay()));
        const smm = String(sun.getMonth() + 1).padStart(2, '0');
        const sdd = String(sun.getDate()).padStart(2, '0');
        const syy = String(sun.getFullYear()).slice(-2);
        sundayUrl = `https://bible.usccb.org/bible/readings/${smm}${sdd}${syy}.cfm`;
        sundayDate = sun.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        const sunRes = await fetch(`/api/usccb-proxy?date=${smm}${sdd}${syy}`);
        if (sunRes.ok) {
          const sunData = await sunRes.json();
          sundayTitle = sunData.title || '';
          sundayBody = sunData.bodyText || '';
        }
      } catch { /* non-critical */ }
    }

    // Step 2: Call JBB to provide readings for this liturgical day
    // The liturgical day name is LOCKED from USCCB — JBB knows the standard lectionary readings for named days
    try {
      const sundayPrompt = sundayTitle
        ? `\nAlso return:\n- sundayLiturgicalDay: "${sundayTitle}"\n- sundayDate: "${sundayDate}"\n- sundayReadings: array of Sunday readings with { "name": "passage reference", "url": "${sundayUrl}" } — First Reading, Responsorial Psalm, Second Reading, Gospel`
        : '\n- sundayLiturgicalDay: null\n- sundayDate: null\n- sundayReadings: null';

      const response = await callJarvis({
        system: 'You are a Catholic lectionary expert. Return ONLY valid JSON, no other text. The liturgicalDay value is provided to you — you MUST use it exactly as given. Do NOT change, rephrase, or override the liturgicalDay under any circumstances.',
        messages: [{
          role: 'user',
          content: `The liturgical day name is EXACTLY: "${todayData.title}"
The USCCB readings page URL is: ${usccbUrl}

For this liturgical day, provide the standard lectionary readings from the Roman Catholic lectionary.

Return JSON with:
- liturgicalDay: "${todayData.title}"
- gospelExcerpt: the first 1-2 sentences of today's Gospel reading (actual scripture text)
- gospelReference: the Gospel passage reference (e.g. "John 20:1-9")
- readings: array of all readings with { "name": "passage reference", "url": "${usccbUrl}" } — include First Reading, Responsorial Psalm, Second Reading (if applicable), Gospel
- url: "${usccbUrl}"${sundayPrompt}
Return ONLY the JSON.`,
        }],
        max_tokens: 700,
      });

      const text = response.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';
      console.log('[Readings] JBB response:', text.substring(0, 300));
      const jsonStr = text.replace(/```json?\n?/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(jsonStr);

      // Force USCCB titles — never trust JBB's liturgical day
      parsed.liturgicalDay = todayData.title;
      if (sundayTitle) parsed.sundayLiturgicalDay = sundayTitle;

      console.log('[Readings] Final:', parsed.liturgicalDay);
      return parsed;
    } catch (err) {
      console.error('[Readings] JBB failed, returning proxy-only data:', err.message);
      // Fallback: use proxy-extracted readings without JBB
      const readingsList = (todayData.readings || []).map(name => ({ name, url: usccbUrl }));
      if (readingsList.length === 0) readingsList.push({ name: 'View today\'s readings on USCCB', url: usccbUrl });
      return {
        liturgicalDay: todayData.title,
        readings: readingsList,
        url: usccbUrl,
        ...(sundayTitle ? {
          sundayLiturgicalDay: sundayTitle,
          sundayDate,
          sundayReadings: [{ name: 'View Sunday readings', url: sundayUrl }],
        } : {}),
      };
    }
  }

  async function generateBriefing() {
    setBriefingLoading(true);
    setBriefingError(null);
    try {
      // Build slim briefing context — readings are already loaded independently
      const briefingCtx = buildBriefingContext({
        nickname,
        today,
        events: eventsForDate(today),
        weekEvents: allWeekEvents,
        missions,
        postIts,
        groceryItems,
        kitchenInventory,
        todayDinnerPlan,
        purchaseAverages,
        activeSorties: [],
      });
      const readingsContext = readings
        ? `\n\nTODAY'S CATHOLIC READINGS:\nLiturgical Day: ${readings.liturgicalDay}\nGospel: "${readings.gospelExcerpt || readings.verse || ''}" (${readings.gospelReference || readings.reference || ''})\n`
        : '';

      // Market conditions check (weekly, non-blocking)
      let marketContext = '';
      try {
        const token = await getToken();
        const mktResult = await checkMarketConditions(token);
        if (mktResult.updated) {
          marketContext = `\n\nMARKET CONDITIONS UPDATE:\nNew Ivans Index data for ${mktResult.month} — ${mktResult.summary}\n`;
        } else if (mktResult.parseError) {
          marketContext = `\n\nMARKET CONDITIONS ALERT:\n⚠️ Ivans Index format may have changed — market conditions panel may be stale. Manual review needed. (${mktResult.message})\n`;
        }
        // If updated: false and no error, say nothing (silent pass)
      } catch { /* market check failure is not critical */ }

      const briefingSystem = briefingCtx + readingsContext + marketContext + `\n\nGENERATE A MORNING BRIEFING for ${nickname || 'the user'}. Be conversational and warm, like a trusted assistant talking to them. Include:
1. A brief reference to today's liturgical day and the verse provided above (weave it in naturally, don't just list it)
2. Overview of today's events with any prep reminders
3. Any tasks that are due or overdue
4. Kitchen status — mention dinner plan, any inventory alerts, and whether a Costco run or cutting session is needed. Keep it brief.
5. If there is a MARKET CONDITIONS UPDATE or ALERT above, mention it briefly — summarize key rate changes or flag the alert. If no market data is provided, skip this entirely.
6. Any upcoming important items this week worth noting
7. Keep it concise — 3-5 short paragraphs max. Not bullet points. Flowing prose.
Address the user by their nickname. Do NOT use markdown formatting — plain text only.`;

      const response = await callJarvis({
        system: briefingSystem,
        messages: [{ role: 'user', content: 'Generate my morning briefing for today.' }],
        max_tokens: 800,
      });

      const text = response.content
        ?.filter(c => c.type === 'text')
        .map(c => c.text)
        .join('') || '';

      const now = new Date();
      setBriefing({ text, generatedAt: now });

      // Save briefing to cache (only BriefingText — never overwrite ReadingsText)
      (async () => {
        try {
          await upsertBriefingCache({ BriefingText: text, BriefingDate: now.toISOString() });
        } catch { /* cache write failure is not critical */ }
      })();
    } catch (err) {
      setBriefingError(err.message);
    } finally {
      setBriefingLoading(false);
    }
  }

  async function startWalkthrough(task) {
    setWalkthroughLoading(true);
    setWalkthroughTask(task);
    try {
      const token = await getToken();
      const docContent = await fetchVaultDocForJarvis(token, task.vaultDocId, task.vaultDocPath);
      setWalkthroughDocContent(docContent);
      setShowJarvis(true);
    } catch (err) {
      setBriefingError(`Couldn't load document: ${err.message}`);
    } finally {
      setWalkthroughLoading(false);
    }
  }

  // Templates view
  if (showTemplates) {
    return (
      <div className="Centcom">
        <MissionTemplates
          getToken={getToken}
          userEmail={userEmail}
          roles={roles}
          procedures={procHook.procedures}
          onClose={() => setShowTemplates(false)}
        />
      </div>
    );
  }

  // FIG Mode — full-screen insurance dashboard
  if (figMode) {
    return <FIGDashboard getToken={getToken} onBack={() => setFigMode(false)} />;
  }

  // Triage Mode — document filing queue
  if (triageMode) {
    return <TriageQueue getToken={getToken} userEmail={userEmail} onBack={() => setTriageMode(false)} onAddClient={() => { setTriageMode(false); setFigMode(true); }} />;
  }

  // Mission Mode — full-screen like Poolside Mode
  if (missionMode) {
    // Gather all pending tasks for today, sorted by calculated time
    const todayEvents = eventsForDate(today);
    const missionTasks = [];
    todayEvents.forEach(ev => {
      const mission = getMission(ev.id);
      if (!mission || mission.skipped) return;
      const allEvTasks = getAllTasks(ev.id);
      allEvTasks.forEach(t => {
        if (t.status !== 'pending') return;
        const eventTime = ev.start ? ev.start.getTime() : 0;
        const taskTime = eventTime + (t.timeOffset || 0) * 60 * 1000;
        const roleObj = roles.find(r => r.id === mission.roleTag);
        missionTasks.push({
          ...t,
          eventId: ev.id,
          eventTitle: ev.title,
          eventTime: ev.start,
          calculatedTime: new Date(taskTime),
          roleColor: roleObj?.color || '#B8860B',
          roleName: roleObj?.name || '',
        });
      });
    });
    missionTasks.sort((a, b) => a.calculatedTime - b.calculatedTime);

    // Count completed/skipped for today
    let totalToday = 0;
    let doneToday = 0;
    let skippedToday = 0;
    todayEvents.forEach(ev => {
      const all = getAllTasks(ev.id);
      totalToday += all.length;
      doneToday += all.filter(t => t.status === 'done').length;
      skippedToday += all.filter(t => t.status === 'skipped').length;
    });
    const completedToday = doneToday + skippedToday;

    const currentTask = missionTasks[0] || null;

    return (
      <div className="Centcom-mission">
        <div className="Centcom-mission-header">
          <span className="Centcom-mission-title">MISSION MODE</span>
          <span className="Centcom-mission-progress">
            {completedToday} of {totalToday} done
          </span>
          <button className="Centcom-mission-exit" onClick={() => setMissionMode(false)}>Overview</button>
        </div>

        {!currentTask ? (
          <div className="Centcom-mission-body">
            <div className="Centcom-mission-done">
              <p className="Centcom-mission-done-text">All caught up!</p>
              <div className="Centcom-mission-done-stats">
                <span>{doneToday} completed</span>
                {skippedToday > 0 && <span>{skippedToday} skipped</span>}
              </div>
              <button className="Centcom-mission-exit" onClick={() => setMissionMode(false)} style={{ marginTop: '2rem' }}>
                Back to Overview
              </button>
            </div>
          </div>
        ) : (
          <div className="Centcom-mission-body">
            {/* Role color bar */}
            <div className="Centcom-mission-role-bar" style={{ background: currentTask.roleColor }} />

            {/* Event context */}
            <div className="Centcom-mission-event-context">
              <span className="Centcom-mission-event-name">{currentTask.eventTitle}</span>
              {currentTask.eventTime && (
                <span className="Centcom-mission-event-time">{formatTime(currentTask.eventTime)}</span>
              )}
            </div>

            {/* Step counter */}
            <div className="Centcom-mission-step">
              Task {completedToday + 1} of {totalToday}
            </div>

            {/* Task name — BIG */}
            <div className="Centcom-mission-task-name">
              {currentTask.name}
            </div>

            {/* Notes */}
            {currentTask.notes && (
              <div className="Centcom-mission-task-notes">
                {currentTask.notes}
              </div>
            )}

            {/* Vault doc links */}
            {currentTask.vaultDocPath && (
              <div className="Centcom-mission-vault-actions">
                <a
                  className="Centcom-mission-vault-link"
                  href={currentTask.vaultDocPath}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Instructions &rarr;
                </a>
                {(currentTask.vaultDocId || currentTask.vaultDocPath) && (
                  <button
                    className="Centcom-mission-walkthrough-btn"
                    onClick={() => startWalkthrough(currentTask)}
                    disabled={walkthroughLoading}
                  >
                    {walkthroughLoading ? 'Loading doc...' : "Ask John's Big Brain to walk me through this \u2192"}
                  </button>
                )}
              </div>
            )}

            {/* Linked procedure */}
            {currentTask.procedureId && (
              <div className="Centcom-mission-vault-actions">
                <button
                  className="Centcom-mission-walkthrough-btn"
                  onClick={() => {
                    const proc = procHook.procedures.find(p => p.id === currentTask.procedureId);
                    if (proc) {
                      const stepsText = proc.steps.map(s => `Step ${s.stepNumber}: ${s.title}\n${s.details}${s.notes ? '\nNote: ' + s.notes : ''}`).join('\n\n');
                      setWalkthroughDocContent({ type: 'text', text: `Procedure: ${proc.name}\n\n${stepsText}`, fileName: proc.name });
                      setWalkthroughTask({ ...currentTask, eventTitle: currentTask.eventTitle });
                      setShowJarvis(true);
                    }
                  }}
                >
                  Start Procedure: {currentTask.procedureName || 'Checklist'} &rarr;
                </button>
              </div>
            )}

            {/* Remaining tasks preview */}
            {missionTasks.length > 1 && (
              <div className="Centcom-mission-up-next">
                <span className="Centcom-mission-up-next-label">Up next:</span>
                <span className="Centcom-mission-up-next-name">{missionTasks[1].name}</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="Centcom-mission-actions">
              <button
                className="Centcom-mission-done-btn"
                onClick={() => updateTaskStatus(currentTask.eventId, currentTask.index, currentTask.isAdditional, 'done')}
              >
                DONE
              </button>
              <button
                className="Centcom-mission-skip-btn"
                onClick={() => updateTaskStatus(currentTask.eventId, currentTask.index, currentTask.isAdditional, 'skipped')}
              >
                SKIP
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Overview Mode
  return (
    <div className="Centcom">
      <div className="Centcom-top-bar">
        <h1 className="Centcom-title">
          CENTCOM
          {weather && (
            <span style={{
              fontSize: '0.65em',
              marginLeft: '0.5em',
              padding: '0.15em 0.5em',
              background: 'rgba(126, 184, 218, 0.18)',
              border: '1px solid rgba(126, 184, 218, 0.35)',
              borderRadius: '6px',
              fontWeight: 500,
              verticalAlign: 'middle',
            }}>
              {weather.high}°/{weather.low}°
            </span>
          )}
        </h1>
        <div className="Centcom-top-bar-btns">
          <button className="Centcom-templates-btn" onClick={() => setShowTemplates(true)}>Templates</button>
          <button className="Centcom-memory-btn" onClick={() => setShowMemoryManager(true)} title="Memory Bank">&#9881;</button>
          {userEmail.toLowerCase() === 'marc@defineholy.com' && (
            <button className="Centcom-mode-btn" onClick={() => setFigMode(true)}>FIG</button>
          )}
          <button className="Centcom-mode-btn" onClick={() => setTriageMode(true)}>Triage</button>
          <button className="Centcom-mode-btn" onClick={() => setMissionMode(true)}>Mission Mode</button>
        </div>
      </div>

      {/* Insurance: one tap to the auto ID card */}
      {onOpenInsurance && (
        <button className="ins-tile" onClick={onOpenInsurance} aria-label="Insurance: auto ID card">
          <span className="ins-tile-icon" aria-hidden="true" />
          <span>
            <span className="ins-tile-label">ID card</span>
            <span className="ins-tile-sub"> · Insurance</span>
          </span>
        </button>
      )}

      {/* Daily Readings Card */}
      {readings && (
        <div className="Centcom-readings-card" style={{ borderLeftColor: liturgicalColor }}>
          <div className="Centcom-readings-card-toggle" onClick={() => updatePref('readingsCollapsed', !readingsCollapsed)}>
            <div className="Centcom-readings-day">
              {readings.liturgicalDay}
              <button onClick={async (e) => {
                e.stopPropagation();
                setReadings(null);
                try {
                  await upsertBriefingCache({ ReadingsText: '' });
                  const readingsData = await fetchReadings();
                  if (readingsData) {
                    setReadings(readingsData);
                    await upsertBriefingCache({ ReadingsText: JSON.stringify(readingsData) });
                  }
                } catch { /* non-critical */ }
              }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888',
                fontSize: '0.7rem', marginLeft: '8px', padding: '2px', verticalAlign: 'middle' }}
                title="Refresh readings">&#8635;</button>
            </div>
            <span className={`Centcom-event-chevron${readingsCollapsed ? '' : ' Centcom-event-chevron--open'}`}>&#9660;</span>
          </div>
          {!readingsCollapsed && (
            <>
              {(readings.gospelExcerpt || readings.verse) && (
                <div className="Centcom-readings-verse">
                  <em>"{readings.gospelExcerpt || readings.verse}"</em>
                  <span className="Centcom-readings-ref"> — {readings.gospelReference || readings.reference}</span>
                </div>
              )}
              {readings.readings && readings.readings.length > 0 && (
                <div className="Centcom-readings-list">
                  {readings.readings.map((r, i) => (
                    <a key={i} className="Centcom-readings-passage" href={r.url || readings.url} target="_blank" rel="noopener noreferrer">
                      {r.name}
                    </a>
                  ))}
                  {readings.sundayLiturgicalDay && readings.sundayReadings && readings.sundayReadings.length > 0 && (
                    <button className="Centcom-readings-passage Centcom-readings-sunday-pill" style={{ color: sundayColor }} onClick={() => setShowSundayReadings(true)}>
                      Sunday &#8250;
                    </button>
                  )}
                </div>
              )}
              {!readings.readings && readings.url && (
                <a className="Centcom-readings-link" href={readings.url} target="_blank" rel="noopener noreferrer">
                  Read full readings &rarr;
                </a>
              )}
            </>
          )}
        </div>
      )}

      {/* Sunday Readings Modal */}
      {showSundayReadings && readings?.sundayReadings && (
        <div className="Centcom-sunday-modal-overlay" onClick={() => setShowSundayReadings(false)}>
          <div className="Centcom-sunday-modal" style={{ borderLeftColor: sundayColor }} onClick={e => e.stopPropagation()}>
            <div className="Centcom-sunday-modal-header">
              <span>{readings.sundayLiturgicalDay}</span>
              <button className="RoleManager-close" onClick={() => setShowSundayReadings(false)}>&times;</button>
            </div>
            {readings.sundayDate && <div className="Centcom-sunday-modal-date">{readings.sundayDate}</div>}
            <div className="Centcom-readings-list">
              {readings.sundayReadings.map((r, i) => (
                <a key={i} className="Centcom-readings-passage" href={r.url || readings.url} target="_blank" rel="noopener noreferrer">
                  {r.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Prayer buttons */}
      <div className="Centcom-prayer-row">
        {PRAYERS.map(prayer => (
          <button key={prayer.id} className="Centcom-prayer-btn" onClick={() => setOpenPrayer(prayer.id)}>
            {prayer.name}
          </button>
        ))}
        {/* Third pill — the family intercessions, a live list, not a PRAYERS entry */}
        <button className="Centcom-prayer-btn" onClick={() => setOpenPrayer('intercessions')}>
          Intercessions
        </button>
        <span className="Centcom-prayer-inscription">In nómine Patris, et Fílii, et Spíritus Sancti. Amen.</span>
      </div>

      {/* Intercessions card (Spirit) */}
      {openPrayer === 'intercessions' && <Intercessions onClose={() => setOpenPrayer(null)} />}

      {/* Prayer modal */}
      {openPrayer && openPrayer !== 'intercessions' && (() => {
        const prayer = PRAYERS.find(p => p.id === openPrayer);
        if (!prayer) return null;
        return (
          <div className="Centcom-prayer-backdrop" onClick={() => setOpenPrayer(null)}>
            <div className="Centcom-prayer-modal" onClick={e => e.stopPropagation()}>
              <button className="Centcom-prayer-close" onClick={() => setOpenPrayer(null)}>&#10005;</button>
              <h2 className="Centcom-prayer-title">{prayer.name}</h2>
              <div className="Centcom-prayer-divider" />
              <div className="Centcom-prayer-text">{prayer.text}</div>
            </div>
          </div>
        );
      })()}

      {/* Morning Briefing */}
      {!briefing && !briefingLoading && (
        <div className="Centcom-card Centcom-briefing">
          <span className="Centcom-briefing-label">Morning Briefing</span>
          <button className="Centcom-briefing-gen-btn" onClick={generateBriefing}>Generate Briefing</button>
        </div>
      )}
      {briefingLoading && (
        <div className="Centcom-card Centcom-briefing Centcom-briefing--loading">
          <span className="Centcom-briefing-label">Morning Briefing</span>
          <span className="Centcom-briefing-loading">John's Big Brain is preparing your briefing...</span>
        </div>
      )}
      {briefingError && <p className="Fitness-error">{briefingError}</p>}
      {briefing && (
        <div className="Centcom-briefing-card">
          <div className="Centcom-briefing-card-header" onClick={() => updatePref('briefingCollapsed', !briefingCollapsed)}>
            <span className="Centcom-briefing-card-title">Morning Briefing</span>
            <div className="Centcom-briefing-card-meta">
              <span className="Centcom-briefing-card-time">
                {briefing.generatedAt instanceof Date
                  ? briefing.generatedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                  : new Date(briefing.generatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
              <button className="Centcom-briefing-refresh" onClick={e => { e.stopPropagation(); generateBriefing(); }}>Refresh</button>
              <span className={`Centcom-event-chevron${briefingCollapsed ? '' : ' Centcom-event-chevron--open'}`}>&#9660;</span>
            </div>
          </div>
          {!briefingCollapsed && (
            <div className="Centcom-briefing-card-body">
              {briefing.text.split('\n').filter(Boolean).map((para, i) => (
                <p key={i} className="Centcom-briefing-para">{para}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Role manager access (filter pills removed — to be rebuilt with proper event filtering) */}
      {!rolesLoading && isAdmin && (
        <button className="Centcom-role-manage-btn" onClick={() => setShowRoleManager(true)} title="Manage Roles">&#9881;</button>
      )}
      {rolesError && <p className="Fitness-error">{rolesError}</p>}
      {missionError && <p className="Fitness-error">{missionError}</p>}

      {showRoleManager && (
        <RoleManager
          allRoles={allRoles}
          onAdd={addRole}
          onEdit={editRole}
          onRemove={removeRole}
          onClose={() => setShowRoleManager(false)}
        />
      )}

      {/* View toggle + navigation */}
      <div className="Centcom-view-toggle">
        <div className="Centcom-toggle-group">
          <button
            className={`Centcom-toggle-btn${viewMode === 'week' ? ' Centcom-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('week')}
          >Week</button>
          <button
            className={`Centcom-toggle-btn${viewMode === 'month' ? ' Centcom-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('month')}
          >Month</button>
        </div>
        {viewMode === 'month' && (
          <button
            type="button"
            className={`Centcom-routine-toggle${showRoutine ? ' Centcom-routine-toggle--on' : ''}`}
            aria-pressed={showRoutine}
            title={showRoutine ? 'Hide routine recurrences' : 'Show routine recurrences'}
            onClick={() => setShowRoutine(v => !v)}
          >Routine</button>
        )}
      </div>

      {viewMode === 'week' && (
        <>
          <div className="Centcom-week-nav">
            <button className="Centcom-week-arrow" onClick={() => setWeekOffset(w => w - 1)}>&lsaquo;</button>
            <span className="Centcom-week-label">{formatMonthRange(weekDays[0], weekDays)}</span>
            <button className="Centcom-week-arrow" onClick={() => setWeekOffset(w => w + 1)}>&rsaquo;</button>
            {weekOffset !== 0 && (
              <button className="Centcom-week-today" onClick={() => setWeekOffset(0)}>Today</button>
            )}
          </div>
          <div className="Centcom-day-strip">
            {weekDays.map((day, i) => {
              const isDayToday = isSameDay(day, today);
              const isSelected = isSameDay(day, selectedDate);
              return (
                <button
                  key={i}
                  className={`Centcom-day-btn${isSelected ? ' Centcom-day-btn--selected' : ''}${isDayToday ? ' Centcom-day-btn--today' : ''}`}
                  onClick={() => setSelectedDate(day)}
                >
                  <span className="Centcom-day-label">{DAY_LABELS[i]}</span>
                  <span className="Centcom-day-num">{day.getDate()}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {viewMode === 'month' && (
        <>
          <div className="Centcom-week-nav">
            <button className="Centcom-week-arrow" onClick={() => setMonthOffset(m => m - 1)}>&lsaquo;</button>
            <span className="Centcom-week-label">
              {new Date(monthYear, monthMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button className="Centcom-week-arrow" onClick={() => setMonthOffset(m => m + 1)}>&rsaquo;</button>
            {monthOffset !== 0 && (
              <button className="Centcom-week-today" onClick={() => setMonthOffset(0)}>Today</button>
            )}
          </div>
          <div className="Centcom-month-header">
            {DAY_LABELS.map(d => <span key={d} className="Centcom-month-header-day">{d}</span>)}
          </div>
          <div className="Centcom-month-grid">
            {monthGridDays.map(({ date: day, inMonth }, i) => {
              const isDayToday = isSameDay(day, today);
              const isSelected = isSameDay(day, selectedDate);
              const dayEvents = eventsForDate(day);
              const runway = runwayForDate(day);
              // Collapse routine recurrences unless the Routine toggle is on.
              const shown = showRoutine ? dayEvents : dayEvents.filter(ev => !isRoutine(ev));
              const routineCount = dayEvents.length - shown.length;
              const overflowCount = Math.max(0, shown.length - MONTH_CELL_MAX_EVENTS);
              // One summary line per cell: overflow of real events and the
              // routine count share it rather than stacking two "+N" chips.
              const summary = [
                overflowCount > 0 && `+${overflowCount}`,
                routineCount > 0 && `${routineCount} routine`,
              ].filter(Boolean).join(' · ');
              return (
                <button
                  key={i}
                  className={`Centcom-month-cell${isSelected ? ' Centcom-month-cell--selected' : ''}${isDayToday ? ' Centcom-month-cell--today' : ''}${!inMonth ? ' Centcom-month-cell--outside' : ''}`}
                  onClick={() => setSelectedDate(day)}
                >
                  <span className="Centcom-month-cell-num">{day.getDate()}</span>
                  {runway && <RunwayBar rw={runway} more={runway.others} onOpen={openRunwayEvent} />}
                  <div className="Centcom-month-cell-events">
                    {shown.slice(0, MONTH_CELL_MAX_EVENTS).map((ev, ei) => (
                      <span key={ei} className="Centcom-month-event" title={ev.title}>
                        {ev.title.length > 12 ? ev.title.substring(0, 12) + '…' : ev.title}
                      </span>
                    ))}
                    {summary && (
                      <span
                        className={`Centcom-month-event ${overflowCount > 0 ? 'Centcom-month-event--more' : 'Centcom-month-event--routine'}`}
                        title={routineCount > 0 ? `${routineCount} routine recurrence${routineCount === 1 ? '' : 's'} hidden — toggle Routine to show` : undefined}
                      >{overflowCount > 0 ? summary : `+${summary}`}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Events for selected day */}
      <div className="Centcom-events">
        {viewMode === 'week' && !calLoading && (
          <RunwayStack
            key={selectedDate.toDateString()}
            runways={runwaysForDate(selectedDate)}
            onOpen={openRunwayEvent}
          />
        )}
        {calLoading && <p className="Centcom-events-loading">Loading calendar...</p>}
        {calError && <p className="Fitness-error">{calError}</p>}
        {!calLoading && !calError && selectedEvents.length === 0 && (
          <p className="Centcom-events-empty">No events on {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
        )}
        {selectedEvents.map(ev => {
          const isExpanded = expandedEventId === ev.id;
          const mission = getMission(ev.id);
          const hasMission = mission && !mission.skipped && (mission.tasks?.length > 0 || mission.additionalTasks?.length > 0);
          const isSkipped = mission && mission.skipped;
          const allTasks = hasMission ? getAllTasks(ev.id) : [];

          return (
            <div key={ev.id} className="Centcom-event-card" onClick={() => setExpandedEventId(isExpanded ? null : ev.id)}>
              <div className="Centcom-event-row">
                {hasMission && <span className="Centcom-event-mission-dot" title="Has mission">&#9632;</span>}
                <span className="Centcom-event-time">{formatTime(ev.start)}</span>
                <div className="Centcom-event-info">
                  <span className="Centcom-event-title">{ev.title}</span>
                  {ev.location && <span className="Centcom-event-location">{ev.location}</span>}
                </div>
                <span className={`Centcom-event-chevron${isExpanded ? ' Centcom-event-chevron--open' : ''}`}>&#9660;</span>
              </div>
              {isExpanded && (
                <div className="Centcom-event-detail" onClick={e => e.stopPropagation()}>
                  {ev.start && ev.end && (
                    <p className="Centcom-event-detail-time">
                      {formatTime(ev.start)} – {formatTime(ev.end)}
                    </p>
                  )}
                  {ev.address && <p className="Centcom-event-detail-addr">{ev.address}</p>}
                  {ev.notes && <p className="Centcom-event-detail-notes">{ev.notes}</p>}

                  {/* Runway lead-up selector — writes a Runway<N> category on the event */}
                  <div className="Centcom-runway-ctl" role="group" aria-label="Runway">
                    <span className="Centcom-runway-ctl-label">Runway</span>
                    {RUNWAY_OPTIONS.map(o => {
                      const active = runwayDaysOf(ev) === o.days;
                      return (
                        <button
                          key={o.days}
                          type="button"
                          className={`Centcom-runway-opt${active ? ' Centcom-runway-opt--active' : ''}`}
                          aria-pressed={active}
                          onClick={() => handleSetRunway(ev, o.days)}
                        >{o.label}</button>
                      );
                    })}
                  </div>

                  {/* Attached mission with task statuses */}
                  {hasMission && (
                    <div className="Centcom-event-mission-attached">
                      <span className="Centcom-event-mission-name">{mission.templateName || 'Mission'}</span>
                      <div className="Centcom-event-mission-tasks">
                        {allTasks.map((t, ti) => (
                          <div key={`${t.isAdditional}-${t.index}`} className={`Centcom-event-task${t.status !== 'pending' ? ' Centcom-event-task--' + t.status : ''}`}>
                            <span className="Centcom-event-task-offset">{formatOffset(t.timeOffset)}</span>
                            <span className="Centcom-event-task-name">{t.name}</span>
                            {t.isAdditional && <span className="Centcom-event-task-tag">added</span>}
                            {t.status === 'pending' && (
                              <div className="Centcom-event-task-btns">
                                <button className="Centcom-task-done-btn" onClick={() => updateTaskStatus(ev.id, t.index, t.isAdditional, 'done')}>Done</button>
                                <button className="Centcom-task-skip-btn" onClick={() => updateTaskStatus(ev.id, t.index, t.isAdditional, 'skipped')}>Skip</button>
                              </div>
                            )}
                            {t.status === 'done' && <span className="Centcom-event-task-status Centcom-event-task-status--done">Done</span>}
                            {t.status === 'skipped' && <span className="Centcom-event-task-status Centcom-event-task-status--skipped">Skipped</span>}
                          </div>
                        ))}
                      </div>

                      {/* Add sub-task */}
                      {addingTaskForEvent === ev.id ? (
                        <div className="Centcom-subtask-form">
                          <input
                            className="Fitness-input"
                            type="text"
                            placeholder="Task name"
                            value={newTaskName}
                            onChange={e => setNewTaskName(e.target.value)}
                            autoFocus
                          />
                          <div className="Centcom-subtask-form-row">
                            <select
                              className="Fitness-select"
                              value={newTaskOffset}
                              onChange={e => setNewTaskOffset(parseInt(e.target.value))}
                            >
                              {TIME_OFFSET_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            <input
                              className="Fitness-input Fitness-input--small"
                              type="text"
                              placeholder="Notes (optional)"
                              value={newTaskNotes}
                              onChange={e => setNewTaskNotes(e.target.value)}
                            />
                          </div>
                          <div className="Centcom-subtask-vault-row">
                            {newTaskVaultDoc ? (
                              <span className="MissionTpl-task-vault">
                                Linked: {decodeURIComponent((newTaskVaultDoc || '').split('/').pop())}
                                <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => setNewTaskVaultDoc('')}>&times;</button>
                              </span>
                            ) : (
                              <button className="MissionTpl-link-doc-btn" onClick={() => setShowSubtaskVaultPicker(true)}>Link Document</button>
                            )}
                          </div>
                          <div className="Centcom-subtask-form-actions">
                            <button className="Fitness-btn Fitness-btn--small" onClick={() => handleAddSubTask(ev.id)}>Add</button>
                            <button className="Fitness-btn Fitness-btn--small Fitness-btn--secondary" onClick={() => { setAddingTaskForEvent(null); setNewTaskVaultDoc(''); }}>Cancel</button>
                          </div>
                          {showSubtaskVaultPicker && (
                            <VaultPicker
                              getToken={getToken}
                              onPick={(file) => { setNewTaskVaultDoc(file.path); setNewTaskVaultDocId(file.id); setShowSubtaskVaultPicker(false); }}
                              onCancel={() => setShowSubtaskVaultPicker(false)}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="Centcom-event-bottom-actions">
                          <button
                            className="Centcom-add-subtask-btn"
                            onClick={() => setAddingTaskForEvent(ev.id)}
                          >+ Add Task</button>
                          <button
                            className="Centcom-add-subtask-btn"
                            onClick={() => setLinkingPostItForEvent(ev.id)}
                          >Link Post-It</button>
                        </div>
                      )}

                      {/* Linked Post-Its */}
                      {mission.postItLinks && mission.postItLinks.length > 0 && (
                        <div className="Centcom-event-postits">
                          <span className="Centcom-event-postits-label">Linked Notes:</span>
                          {mission.postItLinks.map(pid => {
                            const note = postIts.find(p => p.id === pid);
                            return (
                              <div key={pid} className="Centcom-event-postit">
                                <span className="Centcom-event-postit-text">{note ? note.noteText : `Note #${pid}`}</span>
                                <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => unlinkPostIt(ev.id, pid)}>&times;</button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Attach prompt */}
                  {!hasMission && !isSkipped && (
                    <div className="Centcom-event-attach">
                      <button
                        className="Centcom-event-attach-btn"
                        onClick={() => setPickingForEvent(ev)}
                      >Add mission?</button>
                      <button
                        className="Centcom-event-skip-btn"
                        onClick={() => handleSkipMission(ev)}
                      >Skip</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Daily readings — only shown after briefing generation */}

      {/* Template picker overlay */}
      {pickingForEvent && (
        <div className="MissionTpl-picker-overlay" onClick={() => setPickingForEvent(null)}>
          <div className="MissionTpl-picker" onClick={e => e.stopPropagation()}>
            <h3>Pick a template for: {pickingForEvent.title}</h3>
            <MissionTemplates
              getToken={getToken}
              userEmail={userEmail}
              roles={roles}
              procedures={procHook.procedures}
              onPickTemplate={(tmpl) => handlePickTemplate(tmpl, pickingForEvent)}
              onClose={() => setPickingForEvent(null)}
            />
          </div>
        </div>
      )}

      {/* Memory Manager */}
      {showMemoryManager && (
        <MemoryManager
          memory={memory}
          userEmail={userEmail}
          onClose={() => setShowMemoryManager(false)}
        />
      )}

      {/* Post-It picker overlay */}
      {linkingPostItForEvent && (
        <div className="MissionTpl-picker-overlay" onClick={() => setLinkingPostItForEvent(null)}>
          <div className="MissionTpl-picker" onClick={e => e.stopPropagation()}>
            <h3>Link a Post-It Note</h3>
            <div className="Centcom-postit-picker-list">
              {postIts.filter(p => !p.processed).length === 0 && (
                <p className="Centcom-events-empty">No unprocessed Post-It notes.</p>
              )}
              {postIts.filter(p => !p.processed).map(note => (
                <button
                  key={note.id}
                  className="Centcom-postit-picker-item"
                  onClick={async () => {
                    await linkPostIt(linkingPostItForEvent, note.id);
                    setLinkingPostItForEvent(null);
                  }}
                >
                  <span className="Centcom-postit-picker-text">{note.noteText}</span>
                  <span className="Centcom-postit-picker-meta">{note.postedBy}</span>
                </button>
              ))}
            </div>
            <button className="Fitness-btn Fitness-btn--small Fitness-btn--secondary" onClick={() => setLinkingPostItForEvent(null)} style={{ marginTop: '0.5rem' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Jarvis FAB */}
      {!showJarvis && (
        <button className="Jarvis-fab" onClick={() => setShowJarvis(true)} title="Ask John's Big Brain">
          JBB
        </button>
      )}

      {/* Jarvis Chat Panel */}
      {showJarvis && (
        <JarvisChat
          context={jarvisContext}
          walkthroughDoc={walkthroughDocContent}
          walkthroughTask={walkthroughTask}
          memory={memory}
          onClose={() => { setShowJarvis(false); setWalkthroughDocContent(null); setWalkthroughTask(null); }}
          onCreateEvents={async (events) => {
            const token = await getToken();
            const calPath = getCalendarPath();
            if (!calPath) throw new Error('Family calendar not resolved yet');
            for (const ev of events) {
              const body = {
                subject: ev.subject,
                start: { dateTime: ev.startDateTime, timeZone: 'America/Chicago' },
                end: { dateTime: ev.endDateTime, timeZone: 'America/Chicago' },
              };
              if (ev.location) body.location = { displayName: ev.location };
              if (ev.recurrence) body.recurrence = ev.recurrence;
              const createRes = await fetch(
                `https://graph.microsoft.com/v1.0${calPath.base}/calendars/${calPath.id}/events`,
                {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify(body),
                }
              );
              if (!createRes.ok) throw new Error(`Failed to create event (${createRes.status})`);
            }
            refetchCalendar();
          }}
          onAddTask={async (eventTitle, task) => {
            const match = allWeekEvents.find(ev =>
              ev.title.toLowerCase().includes(eventTitle.toLowerCase())
            );
            if (!match) throw new Error(`No matching event found for "${eventTitle}"`);
            await addSubTask(match.id, task);
          }}
          onSearchEmail={async (query, maxResults, recipientFilter) => {
            const token = await getToken();
            return searchEmails(token, query, maxResults, recipientFilter);
          }}
          onReadEmail={async (messageId, attachmentsOnly) => {
            const token = await getToken();
            if (attachmentsOnly) return getEmailAttachments(token, messageId);
            return getEmail(token, messageId);
          }}
          onSaveAttachment={async (messageId, attachmentId, attachmentName, vaultFolder) => {
            const token = await getToken();
            const folderId = vaultFolder ? await resolveVaultFolderPath(token, vaultFolder) : null;
            return saveAttachmentToVault(token, messageId, attachmentId, attachmentName, folderId);
          }}
          onMoveEmail={async (messageId, folderName) => {
            const token = await getToken();
            return moveEmailToFolder(token, messageId, folderName);
          }}
          onSearchVault={async (query) => {
            const token = await getToken();
            // Resolve drive
            const siteRes = await fetch('https://graph.microsoft.com/v1.0/sites/fragosinsurancegroupllc.sharepoint.com:/sites/DefineHolyFamily:', { headers: { Authorization: `Bearer ${token}` } });
            const site = await siteRes.json();
            const drivesRes = await fetch(`https://graph.microsoft.com/v1.0/sites/${site.id}/drives?$select=name,id&$top=100`, { headers: { Authorization: `Bearer ${token}` } });
            const drivesData = await drivesRes.json();
            const drive = (drivesData.value || []).find(d => d.name.toLowerCase() === 'family vault');
            if (!drive) throw new Error('Family Vault not found');
            const searchRes = await fetch(`https://graph.microsoft.com/v1.0/drives/${drive.id}/root/search(q='${encodeURIComponent(query)}')`, { headers: { Authorization: `Bearer ${token}` } });
            if (!searchRes.ok) throw new Error(`Vault search failed (${searchRes.status})`);
            const searchData = await searchRes.json();
            return (searchData.value || []).filter(i => !i.folder).slice(0, 15).map(i => ({
              id: i.id,
              name: i.name,
              folder: i.parentReference?.path?.replace(/.*root:/, '') || '',
              lastModified: i.lastModifiedDateTime || '',
              webUrl: i.webUrl || '',
            }));
          }}
          onReadVaultDoc={async (fileId, fileName) => {
            const token = await getToken();
            return fetchVaultDocForJarvis(token, fileId, '');
          }}
          onSearchProcedures={async (query) => {
            const q = query.toLowerCase();
            return procHook.procedures.filter(p =>
              p.name.toLowerCase().includes(q) ||
              p.category.toLowerCase().includes(q) ||
              p.description.toLowerCase().includes(q)
            );
          }}
          onLoadProcedure={async (procedureId) => {
            return procHook.procedures.find(p => p.id === procedureId) || null;
          }}
          onSearchWorkouts={async (period) => {
            const token = await getToken();
            const items = await getItems(token, 'SwimmingWorkouts');
            const mine = items.filter(it => (it.CreatedByEmail || '').toLowerCase() === userEmail.toLowerCase());
            const now = new Date();
            let cutoff;
            if (period === 'week') cutoff = new Date(now - 7 * 86400000);
            else if (period === 'month') cutoff = new Date(now - 30 * 86400000);
            else if (period === 'year') cutoff = new Date(now - 365 * 86400000);
            else cutoff = new Date(0);
            const filtered = mine.filter(w => new Date(w.WorkoutDate || w.Created || 0) >= cutoff);
            const totalDist = filtered.reduce((s, w) => s + (w.TotalDistanceMeters || 0), 0);
            const totalDur = filtered.reduce((s, w) => s + (w.DurationSeconds || 0), 0);
            const lines = filtered
              .sort((a, b) => new Date(b.WorkoutDate || 0) - new Date(a.WorkoutDate || 0))
              .map(w => `- ${new Date(w.WorkoutDate).toLocaleDateString()}: ${w.TemplateName || 'Workout'} | ${w.TotalDistanceMeters || 0}m | ${Math.round((w.DurationSeconds || 0) / 60)}min${w.WorkoutStartTime ? ' | Start: ' + new Date(w.WorkoutStartTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''} | ID: ${w.id}`)
              .join('\n');
            return `Period: ${period} | Workouts: ${filtered.length} | Total distance: ${totalDist}m | Total time: ${Math.round(totalDur / 60)} minutes\n${lines || 'No workouts found.'}`;
          }}
          onLoadWorkout={async (workoutId) => {
            const token = await getToken();
            const items = await getItems(token, 'SwimmingWorkouts');
            const w = items.find(it => it.id === workoutId);
            if (!w) return 'Workout not found.';
            let sets;
            try { sets = JSON.parse(w.Sets || '[]'); } catch { sets = []; }
            const setLines = sets.map((s, i) => `  ${i + 1}. ${s.actualReps > 1 ? s.actualReps + 'x' : ''}${s.actualDistance || s.distance} ${s.actualStroke || s.stroke}${s.repTimes ? ' [' + s.repTimes.map(t => t + 's').join(', ') + ']' : s.timeSeconds ? ' (' + s.timeSeconds + 's)' : ''}`).join('\n');
            return `Workout: ${w.TemplateName || 'Workout'}\nDate: ${new Date(w.WorkoutDate).toLocaleDateString()}\nTotal: ${w.TotalDistanceMeters || 0}m | Duration: ${Math.round((w.DurationSeconds || 0) / 60)} min\nPool: ${w.PoolLength}${w.PoolUnit}\n${w.WorkoutStartTime ? 'Start: ' + new Date(w.WorkoutStartTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) + '\n' : ''}${w.WorkoutEndTime ? 'End: ' + new Date(w.WorkoutEndTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) + '\n' : ''}Sets:\n${setLines}`;
          }}
          onSearchDinnerOrders={async (period) => {
            const token = await getToken();
            const items = await getItems(token, 'DinnerOrders');
            const now = new Date();
            let cutoff;
            if (period === 'week') cutoff = new Date(now - 7 * 86400000);
            else if (period === 'month') cutoff = new Date(now - 30 * 86400000);
            else if (period === 'year') cutoff = new Date(now - 365 * 86400000);
            else cutoff = new Date(0);
            const filtered = items
              .filter(d => new Date(d.OrderDate || d.Created || 0) >= cutoff)
              .sort((a, b) => (b.OrderDate || '').localeCompare(a.OrderDate || ''));
            if (filtered.length === 0) return `No dinner orders found for period: ${period || 'all'}`;
            const lines = filtered.map(d => {
              let proteinSummary = '';
              try {
                const orders = JSON.parse(d.Protein || '{}');
                if (typeof orders === 'object' && orders !== null && Object.keys(orders).length > 0) {
                  const entries = Object.entries(orders).map(([name, p]) => `${name}: ${p}`).join(', ');
                  const counts = {};
                  Object.values(orders).forEach(p => { counts[p] = (counts[p] || 0) + 1; });
                  const summary = Object.entries(counts).map(([p, c]) => `${c} ${p}`).join(', ');
                  proteinSummary = `${summary} | ${entries}`;
                } else {
                  proteinSummary = d.Protein || 'Steak (all)';
                }
              } catch {
                proteinSummary = d.Protein || 'Steak (all)';
              }
              return `- ${d.OrderDate}: ${d.Headcount || 5} diners at ${d.DinnerTime || '5:00 PM'} | ${proteinSummary}${d.GuestNames ? ' | Guests: ' + d.GuestNames : ''}`;
            });
            return `Period: ${period || 'all'} | ${filtered.length} dinner orders\n${lines.join('\n')}`;
          }}
          onLogPurchase={async (action) => {
            const token = await getToken();
            await createItem(token, 'PurchaseLog', {
              Title: `${action.itemType} x${action.quantity}`,
              PurchaseDate: action.date || new Date().toISOString().split('T')[0],
              ItemType: action.itemType,
              Quantity: action.quantity,
              Cost: action.cost,
              Notes: action.notes || '',
            });
          }}
          onSearchPurchases={async (itemType, period) => {
            const token = await getToken();
            const items = await getItems(token, 'PurchaseLog');
            const now = new Date();
            let cutoff;
            if (period === 'month') cutoff = new Date(now - 30 * 86400000);
            else if (period === 'year') cutoff = new Date(now - 365 * 86400000);
            else cutoff = new Date(0);
            let filtered = items.filter(p => new Date(p.PurchaseDate || p.Created || 0) >= cutoff);
            if (itemType) filtered = filtered.filter(p => (p.ItemType || '').toLowerCase() === itemType.toLowerCase());
            // Group by item type
            const byType = {};
            filtered.forEach(p => {
              const t = p.ItemType || 'Unknown';
              if (!byType[t]) byType[t] = { totalCost: 0, totalQty: 0, entries: [] };
              byType[t].totalCost += p.Cost || 0;
              byType[t].totalQty += p.Quantity || 0;
              byType[t].entries.push(`- ${p.PurchaseDate}: ${p.Quantity} x ${t} = $${p.Cost || 0}`);
            });
            const lines = Object.entries(byType).map(([type, data]) => {
              const avg = data.totalQty > 0 ? (data.totalCost / data.totalQty).toFixed(2) : '0';
              return `${type}: ${data.totalQty} purchased, $${data.totalCost.toFixed(0)} total, $${avg}/unit avg\n${data.entries.join('\n')}`;
            });
            return `Period: ${period || 'all'} | ${filtered.length} purchases\n${lines.join('\n\n') || 'No purchases found.'}`;
          }}
          onAddGroceryItem={async (action) => {
            const token = await getToken();
            const fn = await resolveGroceryFields(token);
            const existing = await getItems(token, 'GroceryList');
            // Check if item already exists (unchecked, same title)
            const match = existing.find(e =>
              (e.Title || '').toLowerCase() === (action.title || '').toLowerCase() &&
              !(e[fn.Checked])
            );
            if (match) {
              // Update priority if it changed
              if (action.priority) {
                await updateItem(token, 'GroceryList', match.id, { [fn.Priority]: action.priority });
              }
            } else {
              const fields = {
                Title: action.title,
                [fn.Checked]: false,
                [fn.Priority]: action.priority || 'green',
              };
              if (action.store) fields[fn.Store] = action.store;
              await createItem(token, 'GroceryList', fields);
            }
          }}
          onUpdateInventory={async (items) => {
            const token = await getToken();
            const existing = await getItems(token, 'KitchenInventory');
            const now = new Date().toISOString();
            for (const item of items) {
              const match = existing.find(e => (e.ItemType || '').toLowerCase() === item.itemType.toLowerCase());
              if (match) {
                await updateItem(token, 'KitchenInventory', match.id, {
                  Quantity: item.quantity,
                  LastUpdated: now,
                });
              } else {
                await createItem(token, 'KitchenInventory', {
                  Title: item.itemType,
                  ItemType: item.itemType,
                  Quantity: item.quantity,
                  LastUpdated: now,
                });
              }
            }

            // Server-side fallback: re-read full inventory, auto-add low items to shopping list
            try {
              const fullInventory = await getItems(token, 'KitchenInventory');
              const getQty = (type) => {
                const item = fullInventory.find(i => (i.ItemType || '').toLowerCase() === type.toLowerCase());
                return item ? (item.Quantity || 0) : 0;
              };
              const loins = getQty('Ribeye Loin');
              const packs = getQty('Sealed Pack');
              const groundBeef = getQty('Ground Beef');
              const wingsLbs = getQty('Wings');
              console.log('[Kitchen] Full inventory after update — loins:', loins, 'packs:', packs, 'groundBeef:', groundBeef, 'wings:', wingsLbs);

              const fn = await resolveGroceryFields(token);
              const groceryItems = await getItems(token, 'GroceryList');

              // Check each protein against minimums
              const checks = [
                { title: 'Ribeye Loins', store: 'Costco', qty: loins, min: 1, supply: (packs * 2) + (loins * 15), rate: 5 },
                { title: 'Ground Beef', store: 'Costco', qty: groundBeef, min: 5, supply: Math.floor(groundBeef / 0.75), rate: 2 },
                { title: 'Wings', store: 'Costco', qty: wingsLbs, min: 5, supply: Math.floor(wingsLbs / 1.5), rate: 2 },
              ];

              for (const check of checks) {
                if (check.qty <= check.min) {
                  const daysOfSupply = Math.floor(check.supply / check.rate);
                  const priority = daysOfSupply <= 1 ? 'red' : daysOfSupply <= 3 ? 'yellow' : 'green';
                  const alreadyOnList = groceryItems.find(g =>
                    (g.Title || '').toLowerCase() === check.title.toLowerCase() && !g[fn.Checked]
                  );
                  console.log(`[Kitchen] ${check.title} below minimum (${check.qty}/${check.min}), priority: ${priority}`);
                  if (alreadyOnList) {
                    await updateItem(token, 'GroceryList', alreadyOnList.id, { [fn.Priority]: priority });
                  } else {
                    await createItem(token, 'GroceryList', {
                      Title: check.title,
                      [fn.Store]: check.store,
                      [fn.Checked]: false,
                      [fn.Priority]: priority,
                    });
                    console.log(`[Kitchen] ${check.title} added to shopping list`);
                  }
                }
              }
            } catch (err) {
              console.error('[Kitchen] Grocery auto-add FAILED:', err.message, err);
            }
          }}
          onUpdateDinnerOrder={async (order) => {
            const token = await getToken();
            const existing = await getItems(token, 'DinnerOrders');
            const match = existing.find(e => e.OrderDate === order.date);
            const fields = { Title: `Dinner ${order.date}` };
            if (order.date) fields.OrderDate = order.date;
            if (order.headcount != null) fields.Headcount = order.headcount;
            if (order.protein) fields.Protein = order.protein;
            if (order.dinnerTime) fields.DinnerTime = order.dinnerTime;
            if (order.guestNames != null) fields.GuestNames = order.guestNames;
            if (match) {
              await updateItem(token, 'DinnerOrders', match.id, fields);
            } else {
              if (!fields.Headcount) fields.Headcount = 5;
              if (!fields.Protein) fields.Protein = 'Steak';
              if (!fields.DinnerTime) fields.DinnerTime = '5:00 PM';
              await createItem(token, 'DinnerOrders', fields);
            }

            // Update existing calendar event if one exists for this date
            try {
              const calPath = getCalendarPath();
              if (calPath) {
                const orderDate = order.date || new Date().toISOString().split('T')[0];
                const dinnerEvent = allWeekEvents.find(ev => {
                  if (!ev.start) return false;
                  const evDate = ev.start.toISOString().split('T')[0];
                  const title = (ev.title || '').toLowerCase();
                  return evDate === orderDate && (title.includes('dinner') || title.includes('steak'));
                });
                if (dinnerEvent) {
                  // Build updated subject from protein orders
                  const headcount = order.headcount || fields.Headcount || 5;
                  let subject = `Dinner — ${headcount} Steaks`;
                  try {
                    const proteinStr = order.protein || fields.Protein || '';
                    const orders = JSON.parse(proteinStr);
                    if (typeof orders === 'object' && orders !== null && Object.keys(orders).length > 0) {
                      const counts = {};
                      Object.values(orders).forEach(p => { counts[p] = (counts[p] || 0) + 1; });
                      const summary = Object.entries(counts).map(([p, c]) => `${c} ${p}`).join(', ');
                      subject = `Dinner — ${summary}`;
                    }
                  } catch { /* old format or not JSON */ }

                  const patch = { subject };

                  // Update time if dinnerTime changed
                  const dinnerTime = order.dinnerTime || fields.DinnerTime;
                  if (dinnerTime) {
                    const timeMatch = dinnerTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                    if (timeMatch) {
                      let hours = parseInt(timeMatch[1]);
                      const mins = timeMatch[2];
                      const ampm = (timeMatch[3] || '').toUpperCase();
                      if (ampm === 'PM' && hours < 12) hours += 12;
                      if (ampm === 'AM' && hours === 12) hours = 0;
                      const h = String(hours).padStart(2, '0');
                      const endH = String(hours + 1).padStart(2, '0');
                      patch.start = { dateTime: `${orderDate}T${h}:${mins}:00`, timeZone: 'America/Chicago' };
                      patch.end = { dateTime: `${orderDate}T${endH}:${mins}:00`, timeZone: 'America/Chicago' };
                    }
                  }

                  await fetch(
                    `https://graph.microsoft.com/v1.0${calPath.base}/calendars/${calPath.id}/events/${dinnerEvent.id}`,
                    {
                      method: 'PATCH',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify(patch),
                    }
                  );
                  console.log('[DinnerOrder] Calendar event updated:', subject);
                  refetchCalendar();
                }
              }
            } catch (err) {
              console.warn('[DinnerOrder] Calendar event update failed (non-critical):', err.message);
            }
          }}
          onCreateDinnerPrep={async (event, tasks) => {
            const token = await getToken();
            const calPath = getCalendarPath();
            if (!calPath) throw new Error('Family calendar not resolved yet');

            const body = {
              subject: event.subject,
              start: { dateTime: event.startDateTime, timeZone: 'America/Chicago' },
              end: { dateTime: event.endDateTime, timeZone: 'America/Chicago' },
            };
            if (event.location) body.location = { displayName: event.location };

            const createRes = await fetch(
              `https://graph.microsoft.com/v1.0${calPath.base}/calendars/${calPath.id}/events`,
              {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
              }
            );
            if (!createRes.ok) throw new Error(`Failed to create dinner event (${createRes.status})`);
            const created = await createRes.json();

            // Attach mission tasks to the new event
            const taskStatus = tasks.map(t => ({
              name: t.name,
              timeOffset: t.timeOffset || 0,
              notes: t.notes || '',
              vaultDocPath: '',
              status: 'pending',
              completedAt: null,
            }));
            await createItem(token, 'EventMissions', {
              Title: 'Dinner Prep',
              EventId: created.id,
              EventTitle: event.subject,
              EventDate: event.startDateTime,
              EventLocation: event.location || '',
              TemplateId: '',
              TaskStatus: JSON.stringify(taskStatus),
              AdditionalTasks: '[]',
              PostItLinks: '[]',
              RoleTag: '',
              CreatedBy: userEmail,
            });

            refetchCalendar();
            await refetchMissions();
          }}
          onProcedureAction={async (action) => {
            if (action.action === 'propose_steps') {
              let proc = procHook.procedures.find(p => p.id === action.procedureId);
              let procId = action.procedureId;

              // Auto-create the procedure if it doesn't exist yet (first batch of a conversion)
              if (!proc) {
                const procName = action.procedureName || action.procedureId || 'New Procedure';
                const created = await procHook.createProcedure({
                  name: procName,
                  category: action.category || '',
                  description: action.description || '',
                  steps: [],
                  sourceDoc: action.sourceDoc || '',
                  conversionStatus: 'in_progress',
                });
                procId = created.id;
                proc = { id: procId, steps: [] };
              }

              const existingSteps = [...(proc.steps || [])];
              const newSteps = (action.steps || []).map(s => ({
                stepNumber: 0,
                title: s.title,
                details: s.details || '',
                notes: s.notes || '',
                lastModified: new Date().toISOString(),
                modifiedBy: userEmail,
              }));
              const allSteps = [...existingSteps, ...newSteps];
              const extraFields = action.isLastBatch
                ? { ConversionStatus: 'complete', ConversionProgress: allSteps.length }
                : { ConversionStatus: 'in_progress', ConversionProgress: allSteps.length };
              await procHook.saveSteps(procId, allSteps, extraFields);
            } else if (action.action === 'edit_procedure_step') {
              await procHook.editStep(action.procedureId, action.stepIndex, action.updates);
            } else if (action.action === 'add_procedure_step') {
              await procHook.addStep(action.procedureId, action.step, action.afterIndex);
            } else if (action.action === 'delete_procedure_step') {
              await procHook.deleteStep(action.procedureId, action.stepIndex);
            } else if (action.action === 'reorder_procedure_step') {
              await procHook.reorderStep(action.procedureId, action.fromIndex, action.toIndex);
            }
          }}
        />
      )}
    </div>
  );
}

export default Centcom;
